import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Chart from 'chart.js/auto';
import { FaChartPie } from "react-icons/fa6";
import { IoBarChart } from "react-icons/io5";
import {
  fmesh,
  fullmesh,
  fmeshPoints,
  foundFeature,
  setFoundFeature,
  animate_speed,
  mapPalette
} from '../../../../core/globalVariables.js';
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { FaExclamationCircle } from "react-icons/fa";
import { IoIosWarning } from "react-icons/io";
import { IoIosInformationCircle } from "react-icons/io";
import MoonLoader from "react-spinners/MoonLoader";

const DynamicStringMetricChart = ({
  metric,
  catgeoryPropertyName,
  keySummary,
  selectedYearEditing,
  thresholdCategorical = 20,
  height = 200,
  longstandingGeoJSONRef,
  selectedKey,
  polygonCustomLayerRef,
  multuGroupSelectedArray,
  setMultuGroupSelectedArray,
  setGlobalPolygonTransparency,
  setSelectedYearEditing,
  availableDates,
  IsLoadingNewGeoJSONFromAnUpdate
}) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const hoveredIndexRef = useRef(null);
  const [chartType, setChartType] = useState('bar');
  const multuGroupSelectedArrayRef = useRef([]);

  useEffect(() => {
    multuGroupSelectedArrayRef.current = multuGroupSelectedArray;
  }, [multuGroupSelectedArray]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleMouseLeave = () => {
      hoveredIndexRef.current = null;
      const hasSelection = (multuGroupSelectedArray?.length ?? 0) > 0;
      const selectedSet = hasSelection
        ? new Set(multuGroupSelectedArray)
        : null;
      fmesh.traverse((child) => {
        if (!child.isMesh || !child.material) return;
        child.material.transparent = true;
        if (!hasSelection) {
          child.material.opacity = 1;
        } else {
          const isSelected = selectedSet.has(child.name);
          child.material.opacity = isSelected ? 1 : 0.3;
        }
        child.material.needsUpdate = true;
      });
      polygonCustomLayerRef.current?.map_repaint();
    };
    canvas.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      canvas.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [multuGroupSelectedArray]);

  const maxIndex = useMemo(
    () => Math.max(0, availableDates.length - 1),
    [availableDates]
  );

  const marks = useMemo(() => {
    if (!longstandingGeoJSONRef.current?.features) return {};

    return availableDates.reduce((acc, date, index) => {
      const targetTime = new Date(date).getTime();

      const hasData = longstandingGeoJSONRef.current.features.some(feature => {
        const series = feature.feature_data_simple_timeseries?.[metric];
        if (!series) return false;

        return Object.entries(series).some(([ts, value]) =>
          new Date(ts).getTime() === targetTime &&
          value !== undefined &&
          value !== null &&
          value !== ""
        );
      });

      if (hasData) {
        acc[index] = new Date(date).getFullYear();
      }

      return acc;
    }, {});
  }, [availableDates, metric]);

  const selectedYearKey = useMemo(() => {
    if (!keySummary?.[metric] || !selectedYearEditing) return null;
    const selectedYear = new Date(selectedYearEditing).getFullYear();
    return Object.keys(keySummary[metric]).find(ts => {
      return new Date(ts).getFullYear() === selectedYear;
    });
  }, [keySummary, metric, selectedYearEditing]);

  const summaryForYear = useMemo(() => {
    if (!selectedYearKey) return null;
    return keySummary?.[metric]?.[selectedYearKey] || null;
  }, [keySummary, metric, selectedYearKey]);

  const hasMetricData = useMemo(() => {
    if (!summaryForYear) return false;

    return Object.keys(summaryForYear).length > 0;
  }, [summaryForYear]);

  const aggregatedData = useMemo(() => {
    if (!summaryForYear) {
      return {
        labels: [],
        amendedData: [],
        isIdField: false,
      };
    }
    const labels = Object.keys(summaryForYear);
    // Treat as an ID field if there are lots of unique values
    if (labels.length > 40) {
      const populated = labels.reduce(
        (sum, label) => sum + (summaryForYear[label] || 0),
        0
      );
      return {
        labels: ["Has ID"],
        amendedData: [populated],
        isIdField: true,
      };
    }
    return {
      labels,
      amendedData: labels.map(label => summaryForYear[label] || 0),
      isIdField: false,
    };
  }, [summaryForYear]);

  // derive whether the currently selected timestamp has data for this metric, then render a small warning icon beside the chart.

  const metricMissingAtSelectedTimestamp = useMemo(() => {
    if (!selectedYearEditing || !longstandingGeoJSONRef.current?.features) {
      return false;
    }
    const targetTime = new Date(selectedYearEditing).getTime();
    return !longstandingGeoJSONRef.current.features.some(feature => {
      const series = feature.feature_data_simple_timeseries?.[metric];
      if (!series) return false;
      return Object.entries(series).some(([ts, value]) =>
        new Date(ts).getTime() === targetTime &&
        value !== undefined &&
        value !== null &&
        value !== ""
      );
    });
  }, [
    selectedYearEditing,
    metric,
    longstandingGeoJSONRef
  ]);

  const stringToHexColor = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return mapPalette[Math.abs(hash) % mapPalette.length];
  };

  useEffect(() => {
    if (
      IsLoadingNewGeoJSONFromAnUpdate ||
      !canvasRef.current ||
      !hasMetricData
    ) {
      return;
    }

    chartRef.current?.destroy();
    const ctx = canvasRef.current.getContext('2d');
    const colors = aggregatedData.labels.map(label =>
      stringToHexColor(label)
    );
    chartRef.current = new Chart(ctx, {
      type: chartType,
      data: {
        labels: aggregatedData.labels,
        datasets: [
          {
            label: 'Amended',
            data: aggregatedData.amendedData,
            backgroundColor: colors,
            borderColor: colors,
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        onHover: (event, elements, chart) => {
          setGlobalPolygonTransparency(1)
          if (elements.length > 0) {
            const index = elements[0].index;
            if (hoveredIndexRef.current !== index) {
              hoveredIndexRef.current = index;
              const label = chart.data.labels[index];
              const value = chart.data.datasets[0].data[index];
              const hoveredValue = chart.data.labels[index];
              const featureLookup = new Map(
                longstandingGeoJSONRef.current.features.map(feature => [
                  String(feature.skylark_id),
                  feature,
                ])
              );
              fmesh.traverse((child) => {
                if (!child.isMesh || !child.material) return;
                const feature = featureLookup.get(String(child.name));
                if (!feature) {
                  child.material.transparent = true;
                  child.material.opacity = 0;
                  return;
                }
                const featureValue = feature?.feature_data_simple_timeseries[selectedKey][selectedYearEditing];
                child.material.transparent = true;
                child.material.opacity =
                  featureValue === hoveredValue ? 1 : 0;
                child.material.needsUpdate = true;
              });
            }
            polygonCustomLayerRef.current.map_repaint();
          } else {
            hoveredIndexRef.current = null;
            const hasSelection = (multuGroupSelectedArray?.length ?? 0) > 0;
            fmesh.traverse((child) => {
              if (!child.isMesh || !child.material) return;
              child.material.transparent = true;
              if (!hasSelection) {
                child.material.opacity = 1;
              } else {
                const selectedSet = new Set(multuGroupSelectedArray);
                const isSelected = selectedSet.has(child.name); // or skylark_id mapping
                child.material.opacity = isSelected ? 1 : 0.3;
              }
              child.material.needsUpdate = true;
            });
            polygonCustomLayerRef.current?.map_repaint();
          }
        },
        plugins: {
          legend: {
            display: chartType === 'pie',
            position: 'right',
            labels: {
              usePointStyle: true,
              pointStyle: 'circle',
              boxWidth: 6,
              boxHeight: 6,
              font: {
                size: 10,
              },
              padding: 4,
            },
          },
        },
        layout: {
          padding: {
            right: 60,
          },
        },
        ...(chartType === 'bar' && {
          scales: {
            x: {
              ticks: {
                display: true,
                font: {
                  size: 10,
                },
                callback: function (value) {
                  const label = this.getLabelForValue(value);
                  return label.length > 12
                    ? label.substring(0, 12) + '...'
                    : label;
                },
              },
              grid: { drawTicks: false },
              title: { display: false },
            },
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Count',
              },
              ticks: {
                font: { size: 9 },
              },
            },
          },
        }),
      },
    });

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [
    aggregatedData,
    chartType,
    height,
    IsLoadingNewGeoJSONFromAnUpdate,
  ]);

  const toIsoWithOffset = (date) => {
    const d = new Date(date);
    const pad = (n) => String(n).padStart(2, '0');
    const year = d.getUTCFullYear();
    const month = pad(d.getUTCMonth() + 1);
    const day = pad(d.getUTCDate());
    const hours = pad(d.getUTCHours());
    const minutes = pad(d.getUTCMinutes());
    const seconds = pad(d.getUTCSeconds());
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}+00:00`;
  };

  return (
    <div>
      <div
        style={{
          height: IsLoadingNewGeoJSONFromAnUpdate
            ? '100px'
            : metricMissingAtSelectedTimestamp
              ? '20px'
              : '100px',
          marginBottom: '40px',
          position: 'relative'
        }}
      >

        {IsLoadingNewGeoJSONFromAnUpdate ? (

          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MoonLoader
              size={30}
              color="#29645D"
            />
          </div>

        ) : metricMissingAtSelectedTimestamp ? (

          <div
            title="No data for this Value at this timestamp"
            style={{
              textAlign: 'left',
              zIndex: 10,
              color: '#fff',
              fontSize: '12px',
              backgroundColor: '#989898',
              paddingLeft: '10px',
              paddingTop: '10px',
              paddingBottom: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              width: 'calc(100% - 30px)',
              borderRadius: '3px',
              marginBottom: '10px',
              marginLeft: '10px',
            }}
          >
            <IoIosInformationCircle
              color="#fff"
              size={20}
              style={{ flexShrink: 0 }}
            />

            <span
              style={{
                textAlign: 'left'
              }}
            >
              No <b>{catgeoryPropertyName}</b> data for selected<br />
              features at {' '}
              <b>
                {new Date(selectedYearEditing).toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric'
                })}
              </b>
            </span>
          </div>

        ) : (

          <>
            <button
              onClick={() =>
                setChartType(prev =>
                  prev === 'bar' ? 'pie' : 'bar',
                )
              }
              style={{
                marginBottom: '30px',
                right: '2.5px',
                float: 'right',
                position: 'absolute',
                fontSize: '18px',
                cursor: 'pointer',
                color: '#29645D',
                zIndex: '1000000'
              }}
            >
              {chartType === 'bar' ? <FaChartPie /> : <IoBarChart />}
            </button>

            <div
              style={{
                height: chartType === 'bar' ? '140px' : '80px',
                paddingTop: chartType === 'bar' ? '0px' : '50px',
                width: '100%',
                overflow: 'hidden',
                position: 'absolute'
              }}
            >
              <canvas
                ref={canvasRef}
                style={{
                  marginTop: chartType === 'bar' ? '0px' : '-50px',
                  maxHeight: '150px'
                }}
              />
            </div>
          </>

        )}

      </div>
    </div>
  );

};

export default DynamicStringMetricChart;